import { Input, Textarea } from "@chakra-ui/react";
import SharedSelect from "../../../../../../../../shared/components/Select";
import { allLanguages } from "../../../../../../../../shared/consts";
import type {
  SummaryDraft,
  SummaryEditableField,
} from "../../../PatientProfileSummarySection.hooks";
import {
  formatClinicalSeverity,
  formatDraftDateValue,
  formatSummaryValue,
  formatSsn,
} from "../personalDetails.utils";
import { SummaryEditableRow } from "./PersonalDetailsRows";

const genderOptions = [
  { value: "F", label: "Female" },
  { value: "M", label: "Male" },
] satisfies Array<{ value: SummaryDraft["gender"]; label: string }>;

const clinicalSeverityLevelOptions = [
  { value: "MINIMAL_ASSISTANCE", label: "Minimal Assistance" },
  { value: "WALKING_ASSISTANCE", label: "Medium Assistance" },
  { value: "HIGHEST_ASSISTANCE", label: "Highest Assistance" },
] satisfies Array<{ value: SummaryDraft["clinicalSeverityLevel"]; label: string }>;

const languageOptions = allLanguages.map((language) => ({ value: language, label: language }));

function getLanguageSelectValue(value: string) {
  return allLanguages.includes(value) ? value : null;
}

type EditableSummaryFieldProps = {
  activeDraft: SummaryDraft | null;
  activeField: SummaryEditableField | null;
  field: SummaryEditableField;
  label: string;
  summaryDraft: SummaryDraft;
  value: React.ReactNode;
  onActivateField: (field: SummaryEditableField) => void;
  onCancelField: () => void;
  onCommitField: () => void;
  onUpdateField: <TKey extends keyof SummaryDraft>(key: TKey, value: SummaryDraft[TKey]) => void;
};

export function EditableTextSummaryField(props: EditableSummaryFieldProps) {
  return (
    <SummaryEditableRow
      activeField={props.activeField}
      field={props.field}
      label={props.label}
      value={props.value}
      onActivate={props.onActivateField}
      onCancel={props.onCancelField}
      onCommit={props.onCommitField}
    >
      <Input
        autoFocus
        size="sm"
        value={String(props.activeDraft?.[props.field as keyof SummaryDraft] ?? "")}
        onChange={(event) =>
          props.onUpdateField(
            props.field as keyof SummaryDraft,
            event.target.value as SummaryDraft[keyof SummaryDraft]
          )
        }
      />
    </SummaryEditableRow>
  );
}

export function EditableSsnSummaryField(
  props: Omit<EditableSummaryFieldProps, "field" | "label" | "value">
) {
  return (
    <SummaryEditableRow
      activeField={props.activeField}
      field="ssn"
      label="Social Security Number (SSN)"
      value={formatSsn(props.summaryDraft.ssn)}
      onActivate={props.onActivateField}
      onCancel={props.onCancelField}
      onCommit={props.onCommitField}
    >
      <Input
        autoFocus
        size="sm"
        value={props.activeDraft?.ssn ?? ""}
        onChange={(event) => props.onUpdateField("ssn", event.target.value)}
      />
    </SummaryEditableRow>
  );
}

export function EditableBirthDateSummaryField(
  props: Omit<EditableSummaryFieldProps, "field" | "label" | "value">
) {
  return (
    <SummaryEditableRow
      activeField={props.activeField}
      field="dateOfBirth"
      label="Birth date"
      value={formatDraftDateValue(props.summaryDraft.dateOfBirth)}
      onActivate={props.onActivateField}
      onCancel={props.onCancelField}
      onCommit={props.onCommitField}
    >
      <Input
        autoFocus
        size="sm"
        type="date"
        value={props.activeDraft?.dateOfBirth ?? ""}
        onChange={(event) => props.onUpdateField("dateOfBirth", event.target.value)}
      />
    </SummaryEditableRow>
  );
}

export function EditableGenderSummaryField(
  props: Omit<EditableSummaryFieldProps, "field" | "label" | "value">
) {
  return (
    <SummaryEditableRow
      activeField={props.activeField}
      cancelOnBlur={false}
      field="gender"
      label="Gender"
      value={formatSummaryValue(props.summaryDraft.gender)}
      onActivate={props.onActivateField}
      onCancel={props.onCancelField}
      onCommit={props.onCommitField}
    >
      <SharedSelect
        label="Select gender"
        multiple={false}
        options={genderOptions}
        value={props.activeDraft?.gender || null}
        onChange={(value) => props.onUpdateField("gender", value ?? "")}
      />
    </SummaryEditableRow>
  );
}

export function EditableLanguageSummaryField(
  props: Omit<EditableSummaryFieldProps, "value"> & { field: "mainLanguage" | "secondaryLanguage" }
) {
  return (
    <SummaryEditableRow
      activeField={props.activeField}
      cancelOnBlur={false}
      field={props.field}
      label={props.label}
      value={props.summaryDraft[props.field]}
      onActivate={props.onActivateField}
      onCancel={props.onCancelField}
      onCommit={props.onCommitField}
    >
      <SharedSelect
        label="Select language"
        multiple={false}
        options={languageOptions}
        value={getLanguageSelectValue(props.activeDraft?.[props.field] ?? "")}
        onChange={(value) => props.onUpdateField(props.field, value ?? "")}
      />
    </SummaryEditableRow>
  );
}

export function EditableClinicalSeveritySummaryField(
  props: Omit<EditableSummaryFieldProps, "field" | "label" | "value">
) {
  return (
    <SummaryEditableRow
      activeField={props.activeField}
      cancelOnBlur={false}
      field="clinicalSeverityLevel"
      label="Clinical severity level"
      value={formatClinicalSeverity(props.summaryDraft.clinicalSeverityLevel || null)}
      onActivate={props.onActivateField}
      onCancel={props.onCancelField}
      onCommit={props.onCommitField}
    >
      <SharedSelect
        label="Select severity"
        multiple={false}
        options={clinicalSeverityLevelOptions}
        value={props.activeDraft?.clinicalSeverityLevel || null}
        onChange={(value) => props.onUpdateField("clinicalSeverityLevel", value ?? "")}
      />
    </SummaryEditableRow>
  );
}

export function EditableAddressInstructionsSummaryField(
  props: Omit<EditableSummaryFieldProps, "field" | "label" | "value">
) {
  return (
    <SummaryEditableRow
      activeField={props.activeField}
      field="addressInstructions"
      label="Address Instructions"
      value={props.summaryDraft.addressInstructions}
      onActivate={props.onActivateField}
      onCancel={props.onCancelField}
      onCommit={props.onCommitField}
    >
      <Textarea
        autoFocus
        rows={3}
        size="sm"
        value={props.activeDraft?.addressInstructions ?? ""}
        onChange={(event) => props.onUpdateField("addressInstructions", event.target.value)}
      />
    </SummaryEditableRow>
  );
}
